import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR, { mutate as mutateCache } from 'swr'
import { audienceImportSessionsApi } from '@/lib/api'
import { useProfile } from '@/lib/hooks'
import type { AudienceCreationFlowSnapshot } from '@digital-twins/shared-types'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import {
  useOptionalImportWizardRealtimeContext,
  useOptionalImportWizardStableContext,
} from '../context/ImportWizardContext'
import { useImportSessionWebsocket } from './useImportSessionWebsocket'
import {
  getAudienceImportContextMaterialsProjectionKey,
  getAudienceImportFlowSnapshotKey,
  getAudienceImportPreviewProjectionKey,
  getAudienceImportSessionKey,
  getAudienceImportWorkbookProjectionKey,
} from '../lib/cache-keys'
import { SESSION_FLOW_SNAPSHOT_REVALIDATION_POLICY } from '../lib/flow-snapshot-revalidation-policy'
import { incrementImportSessionClientMetric } from '../lib/import-session-client-metrics'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import type {
  AudienceImportRouteAck,
  AudienceImportThinRouteAck,
  AudienceImportPreviewProjection,
  AudienceImportSession,
} from '@/types/components/audiences/import-session'
import {
  getRouteAckFromConflictError,
  getRouteAckFromImportSessionEvent,
  mergeRouteAckIntoContextMaterialsProjection,
  mergeRouteAckIntoPreviewProjection,
  mergeRouteAckIntoSession,
  mergeRouteAckIntoWorkbookProjection,
  mergeStatusProjectionIntoSession,
  preferNewerFlowSnapshot,
} from '../lib/import-session-deltas'

const ACTIVE_ANALYSIS_STAGES = new Set(['queued', 'parsing', 'cleaning', 'mapping'] as const)
function isActiveAnalysisStage(stage: string | null | undefined) {
  return typeof stage === 'string' && ACTIVE_ANALYSIS_STAGES.has(stage as any)
}

type AudienceImportAnyRouteAck =
  | AudienceImportRouteAck
  | AudienceImportThinRouteAck

interface UseAudienceImportSessionOptions {
  enabled?: boolean
  fallbackMachineVersion?: number
  subscribeToUpdates?: boolean
}
type SessionMutationKind =
  | 'patch'
  | 'assign_sheets'
  | 'create_audience'
  | 'retry_create'
  | 'retry_analysis'
  | 'discard'
  | 'enter_context_materials'
  | 'complete_skip_context_materials'
  | 'upload_context_material'
  | 'remove_context_material'

function getHighestKnownVersion(...versions: Array<number | null | undefined>) {
  const numericVersions = versions.filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value),
  )
  return numericVersions.length > 0 ? Math.max(...numericVersions) : undefined
}

function shouldPoll(session: AudienceImportSession | undefined) {
  return (
    session?.status === 'analyzing' ||
    session?.status === 'creating' ||
    session?.status === 'finalizing' ||
    isActiveAnalysisStage(session?.analysis?.stage)
  )
}

function isAudienceImportSession(value: unknown): value is AudienceImportSession {
  if (!value || typeof value !== 'object') {
    return false
  }
  return (
    'sessionId' in value &&
    'status' in value &&
    'createdAt' in value &&
    'machineVersion' in value
  )
}

export function useAudienceImportSession(
  sessionId: string,
  options: UseAudienceImportSessionOptions = {},
) {
  const { data: profile } = useProfile()
  const argoEnabled = profile?.featureFlags?.argoAudienceCreation === true

  const wizardStableContext = useOptionalImportWizardStableContext()
  const wizardRealtimeContext = useOptionalImportWizardRealtimeContext()
  const setLastMutationRequestIdFromContext =
    wizardStableContext?.setLastMutationRequestId
  const sessionReadsEnabled = options.enabled !== false
  const shouldSubscribeToUpdates =
    sessionReadsEnabled && options.subscribeToUpdates !== false
  const insideWizardWithSubscribe =
    wizardStableContext !== null && shouldSubscribeToUpdates
  const sessionIdForWebsocket = insideWizardWithSubscribe
    ? ''
    : shouldSubscribeToUpdates
      ? sessionId
      : ''
  const [activeMutation, setActiveMutation] = useState<SessionMutationKind | null>(
    null,
  )
  const [lastMutationRequestId, setLastMutationRequestId] = useState<string | null>(
    null,
  )
  const mutationInFlightRef = useRef(false)
  const pendingSessionInvalidationRef = useRef(false)
  const latestSessionRef = useRef<AudienceImportSession | null>(null)
  const latestSessionIdRef = useRef(sessionId)
  const latestMachineVersionRef = useRef<number | null>(
    options.fallbackMachineVersion ?? null,
  )
  const createRequestIdRef = useRef<string | null>(null)
  const retryCreateRequestIdRef = useRef<string | null>(null)

  useEffect(() => {
    mutationInFlightRef.current = false
    setActiveMutation(null)
    setLastMutationRequestId(null)
    createRequestIdRef.current = null
    retryCreateRequestIdRef.current = null
  }, [sessionId])

  const sessionCacheKey = useMemo(
    () => getAudienceImportSessionKey(sessionId),
    [sessionId],
  )
  const sessionFetcher = useCallback(
    async (
      _key: unknown,
      options?: { signal?: AbortSignal },
    ): Promise<AudienceImportSession> => {
      const signal = options?.signal
      const current = latestSessionRef.current
      if (current && shouldPoll(current)) {
        const progress = await audienceImportSessionsApi.getProgress(sessionId, signal)
        if (progress == null) {
          return audienceImportSessionsApi.get(sessionId, signal)
        }
        const flowKey = getAudienceImportFlowSnapshotKey(sessionId)
        if (flowKey) {
          void mutateCache(
            flowKey,
            (cached) =>
              preferNewerFlowSnapshot(
                cached as AudienceCreationFlowSnapshot | null | undefined,
                progress.flowSnapshot,
              ),
            { revalidate: false },
          )
        }
        const merged = mergeStatusProjectionIntoSession(current, progress)
        return merged ?? current
      }
      return audienceImportSessionsApi.get(sessionId, signal)
    },
    [sessionId],
  )
  const sessionQuery = useSWR<AudienceImportSession>(
    sessionReadsEnabled ? sessionCacheKey : null,
    sessionFetcher,
    {
      refreshInterval: legacyRefreshInterval('IMPORT', 1500),
      revalidateOnFocus: activeMutation === null,
      dedupingInterval: 5000,
    },
  )
  const mutateSessionQuery = sessionQuery.mutate
  const sessionData = sessionQuery.data
  const sessionError = sessionQuery.error as Error | undefined
  const sessionIsLoading = sessionQuery.isLoading
  const sessionIsValidating = sessionQuery.isValidating

  const mutateSession = useCallback(
    async (...args: Parameters<typeof mutateSessionQuery>) => {
      if (!sessionReadsEnabled) {
        return undefined
      }
      return mutateSessionQuery(...args)
    },
    [mutateSessionQuery, sessionReadsEnabled],
  )
  if (latestSessionIdRef.current !== sessionId) {
    latestSessionIdRef.current = sessionId
    latestSessionRef.current = null
    latestMachineVersionRef.current = options.fallbackMachineVersion ?? null
  }
  const renderSession = sessionReadsEnabled ? sessionData ?? null : null
  const renderVersion = renderSession?.machineVersion ?? 0
  const latestVersion = getHighestKnownVersion(
    latestSessionRef.current?.machineVersion,
    latestMachineVersionRef.current,
  ) ?? 0
  if (renderSession && renderVersion >= latestVersion) {
    latestSessionRef.current = renderSession
  }
  latestMachineVersionRef.current = getHighestKnownVersion(
    latestMachineVersionRef.current,
    renderSession?.machineVersion,
  ) ?? null
  const flowSnapshotCacheKey = useMemo(
    () => getAudienceImportFlowSnapshotKey(sessionId),
    [sessionId],
  )
  const contextMaterialsProjectionCacheKey = useMemo(
    () => getAudienceImportContextMaterialsProjectionKey(sessionId),
    [sessionId],
  )
  const previewProjectionCacheKey = useMemo(
    () => getAudienceImportPreviewProjectionKey(sessionId),
    [sessionId],
  )
  const workbookProjectionCacheKey = useMemo(
    () => getAudienceImportWorkbookProjectionKey(sessionId),
    [sessionId],
  )

  const getExpectedVersion = useCallback(() => {
    return getHighestKnownVersion(
      latestSessionRef.current?.machineVersion,
      latestMachineVersionRef.current,
      options.fallbackMachineVersion,
    )
  }, [options.fallbackMachineVersion])

  const requireExpectedVersion = useCallback(() => {
    const v = getExpectedVersion()
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      throw new Error(
        'Import session mutation requires a loaded expected version. ' +
          'Ensure fallbackMachineVersion is passed or session/projection data is loaded.',
      )
    }
    return v
  }, [getExpectedVersion])

  const setKnownMachineVersion = useCallback((version: number | null | undefined) => {
    latestMachineVersionRef.current = getHighestKnownVersion(
      latestMachineVersionRef.current,
      version,
    ) ?? null
  }, [])

  const setCachedSession = useCallback(
    async (next: AudienceImportSession | null | undefined) => {
      latestSessionRef.current = next ?? null
      latestMachineVersionRef.current = getHighestKnownVersion(
        latestMachineVersionRef.current,
        next?.machineVersion,
      ) ?? null
      await mutateSession(next as AudienceImportSession | undefined, {
        revalidate: false,
      })
    },
    [mutateSession],
  )

  const revalidateSession = useCallback(
    async (source: string) => {
      incrementImportSessionClientMetric('session_revalidate', {
        sessionId,
        hook: 'session',
        source,
      })
      const next = await mutateSession()
      if (isAudienceImportSession(next)) {
        latestSessionRef.current = next
        latestMachineVersionRef.current = getHighestKnownVersion(
          latestMachineVersionRef.current,
          next.machineVersion,
        ) ?? null
      }
    },
    [mutateSession, sessionId],
  )

  const revalidateFlowSnapshot = useCallback(async (source: string) => {
    if (!flowSnapshotCacheKey) {
      return
    }
    incrementImportSessionClientMetric('flow_snapshot_revalidate', {
      sessionId,
      hook: 'session',
      source,
    })
    await mutateCache(
      flowSnapshotCacheKey,
      async (current) =>
        preferNewerFlowSnapshot(
          current as AudienceCreationFlowSnapshot | null | undefined,
          await audienceImportSessionsApi.getFlowSnapshot(sessionId, undefined),
        ),
      { revalidate: false, populateCache: true },
    )
  }, [flowSnapshotCacheKey, sessionId])

  const clearFlowSnapshot = useCallback(async () => {
    if (!flowSnapshotCacheKey) {
      return
    }
    await mutateCache(flowSnapshotCacheKey, undefined, { revalidate: false })
  }, [flowSnapshotCacheKey])

  const setCachedFlowSnapshot = useCallback(
    async (
      nextSnapshot: AudienceCreationFlowSnapshot | null | undefined,
      source: string,
    ) => {
      if (!flowSnapshotCacheKey) {
        return
      }
      incrementImportSessionClientMetric('flow_snapshot_local_update', {
        sessionId,
        hook: 'session',
        source,
      })
      if (nextSnapshot == null) {
        await mutateCache(flowSnapshotCacheKey, nextSnapshot, {
          revalidate: false,
        })
        return
      }
      await mutateCache(
        flowSnapshotCacheKey,
        (current) =>
          preferNewerFlowSnapshot(
            current as AudienceCreationFlowSnapshot | null | undefined,
            nextSnapshot,
          ),
        {
          revalidate: false,
        },
      )
    },
    [flowSnapshotCacheKey, sessionId],
  )

  const mergeCachedSessionFromRouteAck = useCallback(
    async (ack: AudienceImportAnyRouteAck) => {
      if (!sessionReadsEnabled) {
        return
      }
      latestMachineVersionRef.current = getHighestKnownVersion(
        latestMachineVersionRef.current,
        ack.machineVersion,
      ) ?? null
      await mutateSession((current) => {
        const merged = mergeRouteAckIntoSession(current, ack)
        return merged ?? current
      }, {
        revalidate: false,
      })
    },
    [mutateSession, sessionReadsEnabled],
  )

  const clearContextMaterialsProjection = useCallback(async () => {
    if (!contextMaterialsProjectionCacheKey) {
      return
    }
    await mutateCache(contextMaterialsProjectionCacheKey, undefined, {
      revalidate: false,
    })
  }, [contextMaterialsProjectionCacheKey])

  const setCachedPreviewProjection = useCallback(
    async (nextProjection: AudienceImportPreviewProjection | null | undefined, source: string) => {
      if (!previewProjectionCacheKey) {
        return
      }
      incrementImportSessionClientMetric('preview_projection_local_update', {
        sessionId,
        hook: 'session',
        source,
      })
      await mutateCache(previewProjectionCacheKey, nextProjection, {
        revalidate: false,
      })
    },
    [previewProjectionCacheKey, sessionId],
  )

  const mergeCachedPreviewProjection = useCallback(
    async (
      ack: AudienceImportAnyRouteAck,
      source: string,
      options?: { audienceName?: string },
    ) => {
      if (!previewProjectionCacheKey) {
        return
      }
      incrementImportSessionClientMetric('preview_projection_local_update', {
        sessionId,
        hook: 'session',
        source,
      })
      await mutateCache(
        previewProjectionCacheKey,
        (current) => mergeRouteAckIntoPreviewProjection(current, ack, options),
        { revalidate: false },
      )
    },
    [previewProjectionCacheKey, sessionId],
  )

  const revalidatePreviewProjection = useCallback(async (source: string) => {
    if (!previewProjectionCacheKey) {
      return
    }
    incrementImportSessionClientMetric('preview_projection_revalidate', {
      sessionId,
      hook: 'session',
      source,
    })
    await mutateCache(previewProjectionCacheKey)
  }, [previewProjectionCacheKey, sessionId])

  const revalidateContextMaterialsProjection = useCallback(async (source: string) => {
    if (!contextMaterialsProjectionCacheKey) {
      return
    }
    incrementImportSessionClientMetric('context_materials_projection_revalidate', {
      sessionId,
      hook: 'session',
      source,
    })
    await mutateCache(contextMaterialsProjectionCacheKey)
  }, [contextMaterialsProjectionCacheKey, sessionId])

  const mergeCachedWorkbookProjection = useCallback(
    async (
      ack: AudienceImportAnyRouteAck,
      source: string,
      options?: {
        audienceName?: string
        sheetAssignment?: {
          audienceSheet: string | null
          dictionarySheet: string | null
          ignoredSheets: string[]
          needsReview: boolean
          options: Array<{
            sheetName: string
            suggestedRole: 'audience' | 'dictionary' | 'ignore'
            rowCount: number | null
            columnCount: number | null
          }>
        } | null
      },
    ) => {
      if (!workbookProjectionCacheKey) {
        return
      }
      incrementImportSessionClientMetric('workbook_projection_local_update', {
        sessionId,
        hook: 'session',
        source,
      })
      await mutateCache(
        workbookProjectionCacheKey,
        (current) => mergeRouteAckIntoWorkbookProjection(current, ack, options),
        { revalidate: false },
      )
    },
    [sessionId, workbookProjectionCacheKey],
  )

  const revalidateWorkbookProjection = useCallback(async (source: string) => {
    if (!workbookProjectionCacheKey) {
      return
    }
    incrementImportSessionClientMetric('workbook_projection_revalidate', {
      sessionId,
      hook: 'session',
      source,
    })
    await mutateCache(workbookProjectionCacheKey)
  }, [sessionId, workbookProjectionCacheKey])

  const mergeCachedContextMaterialsProjection = useCallback(
    async (
      ack: AudienceImportAnyRouteAck,
      source: string,
      options?: { audienceName?: string },
    ) => {
      if (!contextMaterialsProjectionCacheKey) {
        return
      }
      incrementImportSessionClientMetric('context_materials_projection_local_update', {
        sessionId,
        hook: 'session',
        source,
      })
      await mutateCache(
        contextMaterialsProjectionCacheKey,
        (current) =>
          mergeRouteAckIntoContextMaterialsProjection(current, ack, options),
        { revalidate: false },
      )
    },
    [contextMaterialsProjectionCacheKey, sessionId],
  )

  const setCachedRouteAck = useCallback(
    async (ack: AudienceImportAnyRouteAck, source: string) => {
      latestMachineVersionRef.current = getHighestKnownVersion(
        latestMachineVersionRef.current,
        ack.machineVersion,
      ) ?? null
      await setCachedFlowSnapshot(ack.flowSnapshot, source)
    },
    [setCachedFlowSnapshot],
  )

  const scheduleLocalCacheWork = useCallback((operations: Array<Promise<unknown>>) => {
    if (operations.length === 0) {
      return
    }
    void Promise.allSettled(operations)
  }, [])

  const syncFlowSnapshotForMutation = useCallback(
    async (mutationKind: SessionMutationKind) => {
      const policy = SESSION_FLOW_SNAPSHOT_REVALIDATION_POLICY[mutationKind]
      switch (policy.updateStrategy) {
        case 'clear':
          await clearFlowSnapshot()
          return
        case 'server_revalidate':
        default:
          await revalidateFlowSnapshot(mutationKind)
      }
    },
    [clearFlowSnapshot, revalidateFlowSnapshot],
  )

  const queueInvalidation = useCallback(() => {
    pendingSessionInvalidationRef.current = true
  }, [])

  const flushPendingInvalidation = useCallback(async () => {
    if (mutationInFlightRef.current) {
      return
    }
    if (!pendingSessionInvalidationRef.current) {
      return
    }
    pendingSessionInvalidationRef.current = false
    if (sessionReadsEnabled) {
      scheduleLocalCacheWork([revalidateSession('pending_invalidation')])
    }
  }, [revalidateSession, scheduleLocalCacheWork, sessionReadsEnabled])

  const handleSessionInvalidation = useCallback(async (event: ImportSessionUpdatedEvent) => {
    if (mutationInFlightRef.current) {
      queueInvalidation()
      return
    }
    const source = `websocket:${event.reason}`
    const ack = getRouteAckFromImportSessionEvent(event)
    if (ack) {
      scheduleLocalCacheWork([
        setCachedRouteAck(ack, source),
        mergeCachedSessionFromRouteAck(ack),
      ])
      return
    }
    if (sessionReadsEnabled) {
      scheduleLocalCacheWork([revalidateSession(source)])
    }
  }, [mergeCachedSessionFromRouteAck, queueInvalidation, revalidateSession, scheduleLocalCacheWork, sessionReadsEnabled, setCachedRouteAck])

  const { isConnected: isRealtimeConnectedFromWebsocket } = useImportSessionWebsocket(
    sessionIdForWebsocket,
    insideWizardWithSubscribe ? () => {} : handleSessionInvalidation,
    {
      latestMachineVersion: getExpectedVersion() ?? null,
      lastMutationRequestId,
    },
  )
  const isRealtimeConnected = insideWizardWithSubscribe
    ? wizardRealtimeContext?.isRealtimeConnected ?? false
    : isRealtimeConnectedFromWebsocket

  useEffect(() => {
    if (!insideWizardWithSubscribe || !wizardStableContext) return
    return wizardStableContext.registerImportSessionEventHandler(handleSessionInvalidation)
  }, [insideWizardWithSubscribe, wizardStableContext, handleSessionInvalidation])

  const sessionActivePoll = shouldPoll(renderSession ?? undefined)
  useRealtimeRepair({
    active: sessionActivePoll && sessionReadsEnabled,
    onRepair: (reason) => {
      incrementPollWonRepair('IMPORT', reason)
      void revalidateSession(`stall_repair:${reason}`)
    },
  })

  const refetchOnConflict = useCallback(
    async (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== 409) return

      const routeAck = getRouteAckFromConflictError(error)
      if (routeAck) {
        scheduleLocalCacheWork([
          setCachedRouteAck(routeAck, 'conflict'),
          mergeCachedSessionFromRouteAck(routeAck),
          mergeCachedPreviewProjection(routeAck, 'conflict'),
          mergeCachedContextMaterialsProjection(routeAck, 'conflict'),
          mergeCachedWorkbookProjection(routeAck, 'conflict'),
        ])
        return
      }

      const operations: Array<Promise<unknown>> = [
        revalidateFlowSnapshot('conflict'),
        revalidatePreviewProjection('conflict'),
        revalidateContextMaterialsProjection('conflict'),
        revalidateWorkbookProjection('conflict'),
      ]
      if (sessionReadsEnabled) {
        operations.push(revalidateSession('conflict'))
      }
      await Promise.all(operations)
    },
    [
      mergeCachedContextMaterialsProjection,
      mergeCachedPreviewProjection,
      mergeCachedSessionFromRouteAck,
      mergeCachedWorkbookProjection,
      revalidateContextMaterialsProjection,
      revalidateFlowSnapshot,
      revalidatePreviewProjection,
      revalidateSession,
      revalidateWorkbookProjection,
      scheduleLocalCacheWork,
      setCachedRouteAck,
      sessionReadsEnabled,
    ],
  )

  const runWithSingle409Retry = useCallback(
    async <T>(action: (expectedVersion: number) => Promise<T>): Promise<T> => {
      let retried = false
      while (true) {
        const expectedVersion = requireExpectedVersion()
        try {
          return await action(expectedVersion)
        } catch (error) {
          const status = (error as { response?: { status?: number } })?.response
            ?.status
          if (status === 409 && !retried) {
            retried = true
            await refetchOnConflict(error)
            continue
          }
          if (status === 409) {
            await refetchOnConflict(error)
          }
          throw error
        }
      }
    },
    [refetchOnConflict, requireExpectedVersion],
  )

  const runExclusiveMutation = useCallback(
    async <T>(
      mutationKind: SessionMutationKind,
      action: () => Promise<T>,
    ): Promise<T> => {
      if (mutationInFlightRef.current) {
        throw new Error('Another import session action is already in progress.')
      }

      mutationInFlightRef.current = true
      setActiveMutation(mutationKind)
      try {
        return await action()
      } finally {
        mutationInFlightRef.current = false
        setActiveMutation(null)
        void flushPendingInvalidation()
      }
    },
    [flushPendingInvalidation],
  )

  const patchSession = useCallback(
    async (
      payload: Parameters<typeof audienceImportSessionsApi.patch>[1],
    ) => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('patch', async () => {
        try {
          const requestId = `import-session-patch-${sessionId}-${crypto.randomUUID()}`
          setLastMutationRequestId(requestId)
          setLastMutationRequestIdFromContext?.(requestId)
          const ack = await audienceImportSessionsApi.patch(sessionId, {
            ...payload,
            expectedVersion,
          }, requestId)
          const audienceName = payload.audienceDraft?.name
          scheduleLocalCacheWork([
            setCachedRouteAck(ack, 'patch'),
            mergeCachedSessionFromRouteAck(ack),
            mergeCachedPreviewProjection(ack, 'patch', { audienceName }),
            mergeCachedWorkbookProjection(ack, 'patch', { audienceName }),
            mergeCachedContextMaterialsProjection(ack, 'patch', { audienceName }),
          ])
          return ack
        } catch (error) {
          await Promise.all([
            revalidateFlowSnapshot('create_failure'),
            sessionReadsEnabled
              ? revalidateSession('create_failure')
              : Promise.resolve(),
          ])
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [
      sessionId,
      requireExpectedVersion,
      runExclusiveMutation,
      refetchOnConflict,
      mergeCachedContextMaterialsProjection,
      mergeCachedPreviewProjection,
      mergeCachedSessionFromRouteAck,
      mergeCachedWorkbookProjection,
      revalidateFlowSnapshot,
      revalidateSession,
      scheduleLocalCacheWork,
      sessionReadsEnabled,
      setCachedRouteAck,
      setLastMutationRequestIdFromContext,
    ],
  )

  const assignSheets = useCallback(
    async (payload: {
      audienceSheet: string
      dictionarySheet?: string | null
    }) => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('assign_sheets', async () => {
        try {
          const ack = await audienceImportSessionsApi.assignSheets(
            sessionId,
            payload,
            expectedVersion,
          )
          scheduleLocalCacheWork([setCachedRouteAck(ack, 'assign_sheets')])
          track(AnalyticsEvents.AUDIENCE_IMPORT_WORKBOOK_SHEETS_ASSIGNED, {
            session_id: sessionId,
          })
          return ack
        } catch (error) {
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [sessionId, requireExpectedVersion, runExclusiveMutation, refetchOnConflict, scheduleLocalCacheWork, setCachedRouteAck],
  )

  const createAudience = useCallback(
    async (opts?: { activeFilterColumns?: string[]; customLabels?: Record<string, string>; audienceTags?: unknown[] }) => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('create_audience', async () => {
        try {
          const requestId =
            createRequestIdRef.current ??
            `import-session-create-${sessionId}-${crypto.randomUUID()}`
          createRequestIdRef.current = requestId
          setLastMutationRequestIdFromContext?.(requestId)
          const ack = argoEnabled
            ? await audienceImportSessionsApi.createAudienceArgo(
                sessionId,
                expectedVersion,
                requestId,
                opts?.activeFilterColumns,
                opts?.customLabels,
                opts?.audienceTags,
              )
            : await audienceImportSessionsApi.createAudience(
                sessionId,
                expectedVersion,
                requestId,
                opts?.activeFilterColumns,
                opts?.customLabels,
                opts?.audienceTags,
              )
          scheduleLocalCacheWork([setCachedRouteAck(ack, 'create_audience')])
          track(AnalyticsEvents.AUDIENCE_IMPORT_AUDIENCE_CREATION_REQUESTED, {
            session_id: sessionId,
            argo: argoEnabled,
          })
          return ack
        } catch (error) {
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [sessionId, requireExpectedVersion, runExclusiveMutation, refetchOnConflict, scheduleLocalCacheWork, setCachedRouteAck, setLastMutationRequestIdFromContext, argoEnabled],
  )

  const retryCreate = useCallback(
    async () => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('retry_create', async () => {
        try {
          const requestId =
            retryCreateRequestIdRef.current ??
            `import-session-retry-${sessionId}-${crypto.randomUUID()}`
          retryCreateRequestIdRef.current = requestId
          setLastMutationRequestIdFromContext?.(requestId)
          const ack = await audienceImportSessionsApi.retryCreate(
            sessionId,
            expectedVersion,
            requestId,
          )
          scheduleLocalCacheWork([setCachedRouteAck(ack, 'retry_create')])
          return ack
        } catch (error) {
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [sessionId, requireExpectedVersion, runExclusiveMutation, refetchOnConflict, scheduleLocalCacheWork, setCachedRouteAck, setLastMutationRequestIdFromContext],
  )

  const retryAnalysis = useCallback(
    async () => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('retry_analysis', async () => {
        try {
          const ack = await audienceImportSessionsApi.retryAnalysis(
            sessionId,
            expectedVersion,
          )
          scheduleLocalCacheWork([
            setCachedRouteAck(ack, 'retry_analysis'),
            revalidatePreviewProjection('retry_analysis'),
          ])
          return ack
        } catch (error) {
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [sessionId, requireExpectedVersion, runExclusiveMutation, refetchOnConflict, revalidatePreviewProjection, scheduleLocalCacheWork, setCachedRouteAck],
  )

  const discard = useCallback(
    async () => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('discard', async () => {
        try {
          await audienceImportSessionsApi.discard(
            sessionId,
            expectedVersion,
          )
          await Promise.all([
            setCachedSession(undefined),
            setCachedPreviewProjection(undefined, 'discard'),
            clearContextMaterialsProjection(),
            syncFlowSnapshotForMutation('discard'),
          ])
          track(AnalyticsEvents.AUDIENCE_IMPORT_SESSION_DISCARDED, { session_id: sessionId })
        } catch (error) {
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [clearContextMaterialsProjection, sessionId, requireExpectedVersion, runExclusiveMutation, refetchOnConflict, setCachedPreviewProjection, setCachedSession, syncFlowSnapshotForMutation],
  )

  const enterContextMaterials = useCallback(
    async () => {
      return runExclusiveMutation('enter_context_materials', async () => {
        return runWithSingle409Retry(async (expectedVersion) => {
          const ack = await audienceImportSessionsApi.enterContextMaterials(
            sessionId,
            expectedVersion,
          )
          await Promise.all([
            setCachedRouteAck(ack, 'enter_context_materials'),
            mergeCachedSessionFromRouteAck(ack),
            mergeCachedContextMaterialsProjection(ack, 'enter_context_materials'),
            mergeCachedPreviewProjection(ack, 'enter_context_materials'),
          ])
          return ack
        })
      })
    },
    [
      sessionId,
      runExclusiveMutation,
      runWithSingle409Retry,
      mergeCachedContextMaterialsProjection,
      mergeCachedPreviewProjection,
      mergeCachedSessionFromRouteAck,
      setCachedRouteAck,
    ],
  )

  const completeOrSkipContextMaterials = useCallback(
    async (action: 'complete' | 'skip') => {
      return runExclusiveMutation('complete_skip_context_materials', async () => {
        return runWithSingle409Retry(async (expectedVersion) => {
          const ack =
            await audienceImportSessionsApi.completeOrSkipContextMaterials(
              sessionId,
              action,
              expectedVersion,
            )
          scheduleLocalCacheWork([
            setCachedRouteAck(ack, 'complete_skip_context_materials'),
            mergeCachedSessionFromRouteAck(ack),
            mergeCachedContextMaterialsProjection(
              ack,
              'complete_skip_context_materials',
            ),
            mergeCachedPreviewProjection(ack, 'complete_skip_context_materials'),
          ])
          return ack
        })
      })
    },
    [sessionId, runExclusiveMutation, runWithSingle409Retry, mergeCachedContextMaterialsProjection, mergeCachedPreviewProjection, mergeCachedSessionFromRouteAck, scheduleLocalCacheWork, setCachedRouteAck],
  )

  const uploadContextMaterialFile = useCallback(
    async (file: File) => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('upload_context_material', async () => {
        try {
          const response =
            await audienceImportSessionsApi.uploadContextMaterialFile(
              sessionId,
              file,
              expectedVersion,
            )
          if (response.routeAck) {
            await Promise.all([
              setCachedRouteAck(response.routeAck, 'upload_context_material'),
              mergeCachedSessionFromRouteAck(response.routeAck),
              mergeCachedContextMaterialsProjection(
                response.routeAck,
                'upload_context_material',
              ),
              mergeCachedPreviewProjection(response.routeAck, 'upload_context_material'),
            ])
          } else {
            setKnownMachineVersion(response.machineVersion)
            await Promise.all([
              revalidateFlowSnapshot('upload_context_material'),
              revalidateContextMaterialsProjection('upload_context_material'),
              ...(sessionReadsEnabled
                ? [revalidateSession('upload_context_material')]
                : []),
            ])
          }
          return response
        } catch (error) {
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [
      sessionId,
      requireExpectedVersion,
      runExclusiveMutation,
      refetchOnConflict,
      setCachedRouteAck,
      setKnownMachineVersion,
      mergeCachedContextMaterialsProjection,
      mergeCachedPreviewProjection,
      mergeCachedSessionFromRouteAck,
      revalidateFlowSnapshot,
      revalidateContextMaterialsProjection,
      revalidateSession,
      sessionReadsEnabled,
    ],
  )

  const removeContextMaterialFile = useCallback(
    async (fileId: string) => {
      const expectedVersion = requireExpectedVersion()
      return runExclusiveMutation('remove_context_material', async () => {
        try {
          const response =
            await audienceImportSessionsApi.removeContextMaterialFile(
              sessionId,
              fileId,
              expectedVersion,
            )
          if (response.routeAck) {
            scheduleLocalCacheWork([
              setCachedRouteAck(response.routeAck, 'remove_context_material'),
              mergeCachedSessionFromRouteAck(response.routeAck),
              mergeCachedContextMaterialsProjection(
                response.routeAck,
                'remove_context_material',
              ),
              mergeCachedPreviewProjection(response.routeAck, 'remove_context_material'),
            ])
          } else {
            setKnownMachineVersion(response.machineVersion)
            scheduleLocalCacheWork([
              revalidateFlowSnapshot('remove_context_material'),
              revalidateContextMaterialsProjection('remove_context_material'),
              ...(sessionReadsEnabled
                ? [revalidateSession('remove_context_material')]
                : []),
            ])
          }
          return response
        } catch (error) {
          await refetchOnConflict(error)
          throw error
        }
      })
    },
    [
      sessionId,
      requireExpectedVersion,
      runExclusiveMutation,
      refetchOnConflict,
      scheduleLocalCacheWork,
      setCachedRouteAck,
      setKnownMachineVersion,
      mergeCachedContextMaterialsProjection,
      mergeCachedPreviewProjection,
      mergeCachedSessionFromRouteAck,
      revalidateFlowSnapshot,
      revalidateContextMaterialsProjection,
      revalidateSession,
      sessionReadsEnabled,
    ],
  )

  const listContextMaterialFiles = useCallback(async () => {
    try {
      const response =
        await audienceImportSessionsApi.listContextMaterialFilesMinimal(sessionId)
      setKnownMachineVersion(response.machineVersion)
      return response
    } catch (error) {
      await refetchOnConflict(error)
      throw error
    }
  }, [sessionId, refetchOnConflict, setKnownMachineVersion])

  const isMutating = activeMutation !== null
  const ev = getExpectedVersion()
  const expectedVersionLoaded =
    typeof ev === 'number' && Number.isFinite(ev)

  return {
    session: renderSession,
    error: sessionReadsEnabled
      ? sessionError
      : undefined,
    isLoading: sessionReadsEnabled ? sessionIsLoading : false,
    isValidating: sessionReadsEnabled ? sessionIsValidating : false,
    activeMutation,
    isMutating,
    isPatching: activeMutation === 'patch',
    isAssigningSheets: activeMutation === 'assign_sheets',
    isCreating:
      activeMutation === 'create_audience' || activeMutation === 'retry_create',
    isRetryingAnalysis: activeMutation === 'retry_analysis',
    isDiscarding: activeMutation === 'discard',
    isEnteringContextMaterials: activeMutation === 'enter_context_materials',
    isCompletingOrSkippingContextMaterials:
      activeMutation === 'complete_skip_context_materials',
    isUploadingContextMaterial: activeMutation === 'upload_context_material',
    isRemovingContextMaterial: activeMutation === 'remove_context_material',
    isRealtimeConnected,
    lastMutationRequestId,
    expectedVersionLoaded,
    patchSession,
    assignSheets,
    createAudience,
    retryCreate,
    retryAnalysis,
    discard,
    enterContextMaterials,
    completeOrSkipContextMaterials,
    uploadContextMaterialFile,
    removeContextMaterialFile,
    listContextMaterialFiles,
    mutate: mutateSession,
  }
}

export function useAudienceImportSessionMutations(
  sessionId: string,
  options?: {
    fallbackMachineVersion?: number
  },
) {
  const hook = useAudienceImportSession(sessionId, {
    enabled: false,
    fallbackMachineVersion: options?.fallbackMachineVersion,
    subscribeToUpdates: false,
  })

  return {
    error: undefined as Error | undefined,
    expectedVersionLoaded: hook.expectedVersionLoaded,
    activeMutation: hook.activeMutation,
    isMutating: hook.isMutating,
    isPatching: hook.isPatching,
    isAssigningSheets: hook.isAssigningSheets,
    isCreating: hook.isCreating,
    isRetryingAnalysis: hook.isRetryingAnalysis,
    isDiscarding: hook.isDiscarding,
    isEnteringContextMaterials: hook.isEnteringContextMaterials,
    isCompletingOrSkippingContextMaterials:
      hook.isCompletingOrSkippingContextMaterials,
    isUploadingContextMaterial: hook.isUploadingContextMaterial,
    isRemovingContextMaterial: hook.isRemovingContextMaterial,
    lastMutationRequestId: hook.lastMutationRequestId,
    assignSheets: hook.assignSheets,
    createAudience: hook.createAudience,
    retryCreate: hook.retryCreate,
    retryAnalysis: hook.retryAnalysis,
    discard: hook.discard,
    enterContextMaterials: hook.enterContextMaterials,
    completeOrSkipContextMaterials: hook.completeOrSkipContextMaterials,
    uploadContextMaterialFile: hook.uploadContextMaterialFile,
    removeContextMaterialFile: hook.removeContextMaterialFile,
    listContextMaterialFiles: hook.listContextMaterialFiles,
    patchSession: hook.patchSession,
  }
}
